import React, { useState, useMemo, useEffect } from 'react';
import { DynamicInvitedSignaturesFields, InvitedSignatures } from '@dill/dill-shared';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker from './AppDatePicker';
import { UploadFileInputs } from './UploadFileInput';
import RadioInputs from './RadioInput';
import StateDropDown from './StateDropDown';
import CheckboxList from './CheckboxList';

const CustomerSignatureModal = ({
  open,
  handleClose,
  email,
  invitedSignature,
  signature,
  onOpenSignature,
  onSignCreditApplication,
}: {
  open: boolean;
  handleClose: () => void;
  email: string;
  invitedSignature: InvitedSignatures[];
  signature: string;
  onOpenSignature: () => void;
  onSignCreditApplication: (data: {
    dynamicFields: DynamicInvitedSignaturesFields[];
    signature: string;
  }) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [dynamicFieldValues, setDynamicFieldValues] = useState<DynamicInvitedSignaturesFields[]>(
    []
  );
  const [signatureError, setSignatureError] = useState('');
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [rawCurrencyText, setRawCurrencyText] = useState<Record<string, string>>({});

  const formatEIN = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
  };

  const formatSSN = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    if (digits.length > 5) return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return digits;
  };

  const formatPhone = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return digits;
  };

  useEffect(() => {
    const allFields = invitedSignature.filter(
      (sig) => sig.email.trim().toLowerCase() === email.trim().toLowerCase()
    );
    const fields = allFields
      .flatMap((item) => item.fields)
      .map((field) => ({
        ...field,
        id: field?.id.replace(/^(authorized-agent-|guarantor-|other-)/, ''),
      }));
    const uniqueFields = Object.values(
      fields.reduce((acc, field) => {
        acc[field?.id ?? ''] = field;
        return acc;
      }, {} as Record<string, any>)
    );

    const placeHolders = Array.from(new Set(allFields.map((sig) => sig.placeHolder)));
    const placeHolderString =
      placeHolders.length > 1
        ? `${placeHolders.slice(0, -1).join(', ')} and ${placeHolders[placeHolders.length - 1]}`
        : placeHolders.join(', ');

    setCurrentPlaceholder(placeHolderString);

    const initializedFields = uniqueFields.map((field) => ({
      ...field,
      value: field.value ?? '',
    }));
    setDynamicFieldValues(initializedFields);
  }, [invitedSignature, open]);

  const getCurrentFields = (
    groupedField: DynamicInvitedSignaturesFields[],
    columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'
  ): DynamicInvitedSignaturesFields[] => {
    const columnCount = columnType === 'ONE_COLUMN' ? 1 : columnType === 'TWO_COLUMNS' ? 2 : 3;

    if (groupedField.length >= columnCount) {
      return groupedField;
    }

    const missingCount = columnCount - groupedField.length;

    const fillers: DynamicInvitedSignaturesFields[] = Array(missingCount)
      .fill(null)
      .map((_, i) => ({
        name: '',
        id: '',
        fromField: 'TEXT',
        isRequired: false,
        prefix: '',
        suffix: '',
        index: groupedField.length + i,
        value: '',
      }));

    return [...groupedField, ...fillers];
  };

  const updateDynamicFieldValue = (id: string, value: any) => {
    setDynamicFieldValues((prev) =>
      prev.map((field) => (field.id === id ? { ...field, value } : field))
    );
  };

  const validateFields = () => {
    const newErrors: string[] = [];

    for (const field of dynamicFieldValues) {
      if (field.isRequired && (field.value === '' || field.value == null)) {
        newErrors.push(field.id);
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSignCreditApplication = () => {
    if (!validateFields()) return;
    if (signature.trim() === '') {
      setSignatureError('Signature is required');
      return;
    }

    onSignCreditApplication({
      dynamicFields: dynamicFieldValues,
      signature,
    });
  };

  const fieldsGroup = useMemo(() => {
    const grouped = dynamicFieldValues.reduce((acc, item) => {
      if (!acc[item.index]) acc[item.index] = [];
      acc[item.index].push(item);
      return acc;
    }, {} as Record<number, typeof dynamicFieldValues>);

    const groupedArray = Object.values(grouped).map((group) => {
      const columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS' =
        group.length === 1 ? 'ONE_COLUMN' : group.length === 2 ? 'TWO_COLUMNS' : 'THREE_COLUMNS';

      return group.map((field) => ({
        ...field,
        columnType,
      }));
    });

    return groupedArray;
  }, [dynamicFieldValues]);

  useEffect(() => {
    if (open) {
      setLoading(false);
      setErrors([]);
      setInlineErrors({});
      setRawCurrencyText({});
      setSignatureError('');
    }
  }, [open]);

  const handleCloseModal = () => {
    setLoading(false);
    setErrors([]);
    setInlineErrors({});
    setRawCurrencyText({});
    setSignatureError('');
    setDynamicFieldValues([]);
    handleClose();
  };

  const renderSignatureFields = (fields: DynamicInvitedSignaturesFields[]) => {
    return fields.map((field, idx) => {
      const currentValue = dynamicFieldValues.find((f) => f.id === field.id)?.value;
      const hasError = errors.includes(field.id);
      let fieldComponent = null;

      switch (field.fromField) {
        case 'TEXT':
        case 'NUMBER':
        case 'CURRENCY':
        case 'TEXT_AREA': {
          const currencyDecimalPlaces: number = field.currencyDecimalPlaces ?? 2;
          fieldComponent = (
            <>
              <AppInputField
                label={field.name}
                inputType={
                  field.fromField === 'TEXT_AREA'
                    ? 'textarea'
                    : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                    ? 'number'
                    : 'text'
                }
                value={
                  field.fromField === 'CURRENCY'
                    ? rawCurrencyText[field.id] !== undefined
                      ? rawCurrencyText[field.id]
                      : typeof currentValue === 'number'
                      ? currentValue.toLocaleString('en-US', {
                          minimumFractionDigits: currencyDecimalPlaces,
                          maximumFractionDigits: currencyDecimalPlaces,
                        })
                      : ''
                    : currentValue || ''
                }
                isRequired={field.isRequired}
                suffix={field.suffix}
                prefix={field.fromField === 'CURRENCY' ? field.prefix || '$' : field.prefix}
                placeholder={
                  field.validationType === 'EIN'
                    ? 'XX-XXXXXXX'
                    : field.validationType === 'SSN'
                    ? 'XXX-XX-XXXX'
                    : field.validationType === 'PHONE'
                    ? 'XXX-XXX-XXXX'
                    : undefined
                }
                maxLength={
                  field.validationType === 'EIN'
                    ? 10
                    : field.validationType === 'SSN'
                    ? 11
                    : field.validationType === 'PHONE'
                    ? 12
                    : field.maxLength
                }
                onTextChange={(text) => {
                  setErrors((prev) => prev.filter((error) => error !== field.id));
                  if (field.fromField === 'CURRENCY') {
                    const stripped = text.replace(/[$,]/g, '');
                    if (!/^\d*\.?\d*$/.test(stripped)) return;
                    if (currencyDecimalPlaces === 0 && stripped.includes('.')) return;
                    const decMatch = stripped.match(/\.(\d+)/);
                    if (decMatch && decMatch[1].length > currencyDecimalPlaces) return;
                    const intPart = stripped.split('.')[0];
                    const hasDecimal = stripped.includes('.');
                    const decPart = stripped.split('.')[1] ?? '';
                    const formattedInt = intPart
                      ? parseInt(intPart, 10).toLocaleString('en-US')
                      : '';
                    const displayValue = formattedInt + (hasDecimal ? '.' + decPart : '');
                    setRawCurrencyText((prev) => ({ ...prev, [field.id]: displayValue }));
                    const numericValue = parseFloat(stripped);
                    updateDynamicFieldValue(
                      field.id,
                      stripped === '' || isNaN(numericValue) ? null : numericValue
                    );
                    return;
                  }
                  let formatted = text;
                  if (field.fromField === 'TEXT') {
                    if (field.validationType === 'EIN') {
                      formatted = formatEIN(text);
                      if (formatted.length === 10) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [field.id]: /^\d{2}-\d{7}$/.test(formatted)
                            ? ''
                            : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                        }));
                      } else if (inlineErrors[field.id]) {
                        setInlineErrors((prev) => ({ ...prev, [field.id]: '' }));
                      }
                    } else if (field.validationType === 'SSN') {
                      formatted = formatSSN(text);
                      if (formatted.length === 11) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [field.id]: /^\d{3}-\d{2}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                        }));
                      } else if (inlineErrors[field.id]) {
                        setInlineErrors((prev) => ({ ...prev, [field.id]: '' }));
                      }
                    } else if (field.validationType === 'PHONE') {
                      formatted = formatPhone(text);
                      if (formatted.length === 12) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [field.id]: /^\d{3}-\d{3}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                        }));
                      } else if (inlineErrors[field.id]) {
                        setInlineErrors((prev) => ({ ...prev, [field.id]: '' }));
                      }
                    }
                  }
                  updateDynamicFieldValue(field.id, formatted);
                }}
                onBlur={() => {
                  if (field.fromField === 'CURRENCY') {
                    setRawCurrencyText((prev) => {
                      const next = { ...prev };
                      delete next[field.id];
                      return next;
                    });
                    return;
                  }
                  if (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') {
                    const val = typeof currentValue === 'string' ? currentValue.trim() : '';
                    updateDynamicFieldValue(field.id, val);
                    if (val && field.validationType === 'EIN') {
                      setInlineErrors((prev) => ({
                        ...prev,
                        [field.id]: /^\d{2}-\d{7}$/.test(val)
                          ? ''
                          : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                      }));
                    } else if (val && field.validationType === 'SSN') {
                      setInlineErrors((prev) => ({
                        ...prev,
                        [field.id]: /^\d{3}-\d{2}-\d{4}$/.test(val)
                          ? ''
                          : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                      }));
                    } else if (val && field.validationType === 'PHONE') {
                      setInlineErrors((prev) => ({
                        ...prev,
                        [field.id]: /^\d{3}-\d{3}-\d{4}$/.test(val)
                          ? ''
                          : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                      }));
                    }
                  }
                }}
                errorText={
                  inlineErrors[field.id] || (hasError ? `${field.name} is required` : undefined)
                }
                dataPrivate={field?.isHashed ? true : false}
              />
              {field.validationType === 'EIN' && !inlineErrors[field.id] && (
                <p className="text-xs mt-0.5 text-gray-400">Format: XX-XXXXXXX (e.g. 12-3456789)</p>
              )}
              {field.validationType === 'SSN' && !inlineErrors[field.id] && (
                <p className="text-xs mt-0.5 text-gray-400">
                  Format: XXX-XX-XXXX (e.g. 123-45-6789)
                </p>
              )}
              {field.validationType === 'PHONE' && !inlineErrors[field.id] && (
                <p className="text-xs mt-0.5 text-gray-400">
                  Format: XXX-XXX-XXXX (e.g. 555-123-4567)
                </p>
              )}
            </>
          );
          break;
        }

        case 'DATE':
          fieldComponent = (
            <AppDatePicker
              label={field.name}
              selectedDate={currentValue || null}
              isRequired={field.isRequired}
              onDateSelected={(date) => {
                updateDynamicFieldValue(field.id, date);
                setErrors((prev) => prev.filter((error) => error !== field.id));
              }}
              errorText={hasError ? `${field.name} is required` : undefined}
            />
          );
          break;

        case 'FILE':
          fieldComponent = (
            <div className="my-1 flex items-center w-full">
              <UploadFileInputs
                isRequired={field.isRequired}
                label={field.name}
                onSelectFile={(file) => {
                  updateDynamicFieldValue(field.id, file.file);
                  setErrors((prev) => prev.filter((error) => error !== field.id));
                }}
                errorText={hasError ? 'File is required' : undefined}
                selectedFile={currentValue || null}
              />
            </div>
          );
          break;

        case 'OPTION_FIELD':
          fieldComponent = (
            <div className="my-1 flex items-center w-full">
              <RadioInputs
                label={field.name}
                onChange={(val) => {
                  updateDynamicFieldValue(field.id, val);
                  setErrors((prev) => prev.filter((error) => error !== field.id));
                }}
                id={field.name}
                options={
                  field?.optionList
                    ? field.optionList.split(',').map((option) => option.trim())
                    : []
                }
                isRequired={field.isRequired}
                selectedOption={currentValue || ''}
                className={field.isRow ? 'gap-2 flex items-center' : ''}
                errorText={hasError ? 'Please select an option' : undefined}
              />
            </div>
          );
          break;

        case 'STATE_DROPDOWN':
          fieldComponent = (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={currentValue || ''}
              errorText={hasError ? `${field.name} is required` : undefined}
              onTextChange={(text) => {
                updateDynamicFieldValue(field.id, text);
              }}
            />
          );
          break;

        case 'CHECKBOX_LIST':
          fieldComponent = (
            <CheckboxList
              isRequired={field.isRequired}
              label={field.name}
              options={
                field?.optionList ? field?.optionList.split(',').map((option) => option.trim()) : []
              }
              value={currentValue || ''}
              errorText={hasError ? `${field.name} is required` : undefined}
              onTextChange={(text) => {
                updateDynamicFieldValue(field.id, text);
              }}
            />
          );
          break;

        default:
          fieldComponent = (
            <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
          );
          break;
      }

      return (
        <div
          key={idx}
          className={`
                flex-shrink-0
                flex-grow
                basis-[280px]
                sm:basis-[45%]
                lg:basis-[30%]
                max-w-full
              `}>
          <div className="px-1 py-2 w-full">{fieldComponent}</div>
        </div>
      );
    });
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Complete & Sign"
      closeOnOverlayClick={false}
      maxWidth="600px"
      footer={
        <>
          <DillButton
            onClick={handleCloseModal}
            variant="secondary"
            label="Close"
            className="flex justify-center"
          />
          <DillButton
            onClick={handleSignCreditApplication}
            label="Submit"
            isLoading={loading}
            isDisabled={loading}
            className="flex justify-center"
          />
        </>
      }>
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-3.5 py-2.5">
        <p className="text-xs text-blue-700 leading-relaxed">
          You have been added as a <span className="font-semibold">{currentPlaceholder}</span>.
          Please fill in the required details and add your signature below before submitting.
        </p>
      </div>
      {fieldsGroup.length > 0 && (
        <div className="w-full">
          {fieldsGroup.map((group, index) => (
            <div key={`signature_preview_group_${index}`} className="w-full flex flex-wrap">
              {renderSignatureFields(
                getCurrentFields(group, group?.[0]?.columnType || 'ONE_COLUMN')
              )}
            </div>
          ))}
        </div>
      )}
      <div className="w-full">
        <div className="w-full flex gap-4 items-center">
          {signature.trim() !== '' && <img className="h-[30px] w-auto" src={signature} />}
          <DillButton
            label={signature.trim() ? 'Edit Signature' : 'Add Signature'}
            onClick={() => {
              setSignatureError('');
              onOpenSignature();
            }}
            height={35}
          />
        </div>
        {signatureError && <p className="text-xs mt-0.5 text-red-500">{signatureError}</p>}
      </div>
    </DillModal>
  );
};

export default CustomerSignatureModal;
